import { getAuth, signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { ref, set, getDatabase, onValue } from 'firebase/database';
import { populateProfileData } from '../utils/firebase';
import axios from 'axios';
import bowser from 'bowser';
import app from '../utils/firebase';

const auth = getAuth(app);

const errorMessages = {
    'auth/wrong-password': 'The password is invalid.',
    'auth/user-not-found': 'There is no user record corresponding to this email.',
    'auth/invalid-credential': 'The credentials are invalid or user does not exist.',
    'auth/email-already-in-use': 'The email address is already in use by another account.',
    'auth/invalid-email': 'The email address is not valid.',
    'auth/operation-not-allowed': 'Email/password accounts are not enabled. Please contact support.',
    'auth/weak-password': 'The password is too weak.',
    'auth/too-many-requests': 'Too many unsuccessful login attempts. Please try again later.',
};

class AuthService {
    constructor(stateManager) {
        this.setMessage = stateManager.setMessage;
    }

    isEmailValid = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    handleLogin = async (values) => {
        const { email, password } = values;

        if (!email || !email.trim() || !this.isEmailValid(email)) {
            throw new Error("Please provide a valid email address");
        }

        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            if (!userCredential.user) {
                throw new Error("Authentication failed");
            }
            
            // Log login event
            await this.logLoginEvent();
            return userCredential.user;
        } catch (error) {
            console.error('Firebase auth error:', error);
            throw new Error(errorMessages[error.code] || error.message);
        } 
    };

    handleGoogleSignIn = async () => {
        const provider = new GoogleAuthProvider();        
        try {
            const result = await signInWithPopup(auth, provider);
            if (!result.user) {
                throw new Error("Google authentication failed");
            }
            
            // Log login event
            await this.logLoginEvent();
            return result.user;
        } catch (error) {
            console.error('Google auth error:', error);
            throw new Error(errorMessages[error.code] || error.message);
        }
    };

    logLoginEvent = async () => {
        const user = auth.currentUser;
        if (!user) return;

        try {
            if (user) {
                const timestamp = new Date().toISOString();
                const browserInfo = this.getBrowserInfo();
                const { city, country } = await this.getLocationInfo();
                const browserName = browserInfo.browser.name + ' ' + browserInfo.os.name
                const loginData = {
                    timestamp,
                    browserName,
                    city,
                    country
                }
                const db = getDatabase();
                const loginRef = ref(db, `users/${user.uid}/loginHistory`);

                onValue(loginRef, (snapshot) => {
                    const data = snapshot.val();
                    const history = data ? Object.values(data) : [];
                    const lastLogin = history[history.length - 1];
                    if (!lastLogin || lastLogin.city !== city || lastLogin.country !== country) {
                        const updatedInfo = ref(db, `users/${user.uid}/loginHistory/${new Date()}`);
                        set(updatedInfo, loginData);
                    }
                });
            }
        } catch (error) {
            console.error("Error logging login event:", error);
        }
    };

    getLocationInfo = async () => {
        try {
            const response = await axios.get('https://us-central1-finance-ebe5a.cloudfunctions.net/locationInfo');
            return response.data;
        } catch (error) {
            return { city: 'Unknown', country: 'Unknown' };
        }
    };

    getBrowserInfo = () => {
        const browser = bowser.getParser(window.navigator.userAgent);
        return browser.getResult();
    };

    handleRegister = async(fields) => {
        var userData = {
          name: fields.name,
          email: fields.email,
          phoneNumber: null,
          subscriptionStatus: "free"
        };
    
        try {
          const result = await createUserWithEmailAndPassword(auth, fields.email, fields.password);
            await sendEmailVerification(result.user);
            await populateProfileData(`users/${result.user.uid}/profile`, userData);
            return { success: true, user: result.user };
        } catch (error) {
            throw new Error(errorMessages[error.code] || error.message);
        }
      };
}

export const createAuthService = (stateManager) => new AuthService(stateManager);
